import {
  Button,
  cn,
  focusStyles,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@beweegbaas/ui";
import { DragDropProvider, useDroppable, type DragEndEvent } from "@dnd-kit/react";
import { useAtomSet } from "@effect/atom-react";
import { Option } from "effect";
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import {
  RoosterCel,
  type DropData,
  type Klas,
} from "#/modules/les.planning/components/rooster-cel";
import { labelToMinutes, minutesToLabel } from "#/modules/les.planning/domain/calendar";
import {
  addLesuur,
  addPauze,
  formatLesuur,
  getDagrooster,
  getMatchingLesuur,
  getRijen,
  insertPauze,
  moveBegin,
  movePauze,
  removeLesuur,
  removePauze,
  renamePauze,
  resizeEinde,
  SCHOOLDAGEN,
  withDagrooster,
  type Dagrooster,
  type Lesrooster,
  type Lesuur,
  type Pauze,
  type RoosterRij,
} from "#/modules/les.planning/domain/lesrooster";
import type { RoosterDocent, Roosterregel } from "#/modules/les.planning/domain/rooster";
import {
  changeLesroosterAtom,
  commitLesroosterAtom,
  lesroosterDraftAtom,
  moveRegelAtom,
} from "#/modules/les.planning/rooster/rooster-editor";
import { weekdagSchema } from "#/modules/les.planning/schema.search";
import { randomUuid } from "#/modules/util.helpers/utils.uuid";

const weekdagFormatter = new Intl.DateTimeFormat("nl-NL", { weekday: "long" });
const weekdagKortFormatter = new Intl.DateTimeFormat("nl-NL", { weekday: "short" });

// 2024-01-01 is a Monday.
const weekdagName = (weekdag: number) => weekdagFormatter.format(new Date(2024, 0, weekdag));
const weekdagKort = (weekdag: number) =>
  weekdagKortFormatter.format(new Date(2024, 0, weekdag)).replace(".", "");

// The head of a row starts at the page's edge and keeps the gutter inside it, so its hover and
// the outline of a picked row run up to that edge.
// On iOS both times are 0.75rem wider (TijdVeld).
const KOP =
  "w-[calc(11.5rem+var(--rooster-rand))] shrink-0 pl-(--rooster-rand) supports-[-webkit-touch-callout:none]:w-[calc(13rem+var(--rooster-rand))]";
// A row reaches the page's edges; its content stays inside the gutter and clear of the card
// that floats on the right (`--rooster-vrij`).
const RIJ = "-mx-(--rooster-rand) pr-[calc(var(--rooster-rand)+var(--rooster-vrij,0px))]";
// The rows are as tall as they last, a standard lesuur of 45 minutes the 50 pixels of the design;
// dragging an edge moves it by the same scale, so it stays under the pointer.
const PX_PER_MINUUT = 50 / 45;
const MIN_HOOGTE = { lesuur: 36, pauze: 40 } as const;
// A gap between two rows shows, up to this many pixels.
const MAX_GAT = 40;
const STAP = 5;

const duurVan = (rij: RoosterRij) => rij.eindMinuten - rij.startMinuten;
const hoogteVan = (rij: RoosterRij) =>
  Math.max(MIN_HOOGTE[rij.kind], Math.round(duurVan(rij) * PX_PER_MINUUT));
const SLEEP_DREMPEL = 4;
// As long, and as still, as dnd-kit holds a leerling in the groepjes before it moves.
const HOUD_MS = 250;
const HOUD_SPELING = 5;

const rijNaam = (rij: RoosterRij) =>
  rij.kind === "lesuur" ? formatLesuur(rij.lesuur) : rij.pauze.naam;

type Sleep =
  | { readonly kind: "verplaats"; readonly id: string; readonly voor: number }
  | {
      readonly kind: "rand";
      readonly id: string;
      readonly rand: "begin" | "einde";
      /** The row as it was when the drag began. */
      readonly basis: RoosterRij;
    };

/**
 * The rooster of one weekdag as a table of that day: its lesuren and pauzes are the rows, with
 * their times on the left, and every lesuur holds the lessen of the school then. A row is picked
 * to drag its edges, a pauze is dragged by its handle to another place, and a pauze goes in
 * between two lesuren from the line between them. A les is dragged to another lesuur, or onto the
 * tab of another day.
 */
export function RoosterTabel({
  lesrooster,
  weekdag,
  regels,
  klassen,
  docenten,
  onWeekdagChange,
}: {
  lesrooster: Lesrooster;
  weekdag: number;
  regels: readonly Roosterregel[];
  klassen: readonly Klas[];
  docenten: readonly RoosterDocent[];
  onWeekdagChange: (weekdag: number) => void;
}) {
  const commit = useAtomSet(commitLesroosterAtom);
  const setDraft = useAtomSet(lesroosterDraftAtom);
  const moveRegel = useAtomSet(moveRegelAtom);
  const dagrooster = getDagrooster(lesrooster, weekdag);
  const rijen = getRijen(dagrooster);
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [sleep, setSleep] = useState<Sleep | null>(null);
  const rijRefs = useRef(new Map<string, HTMLDivElement>());
  const tabel = useRef<HTMLDivElement>(null);
  const commitDagrooster = (next: Dagrooster) => commit(withDagrooster(lesrooster, next));

  // A click anywhere outside the rows lets the picked row go.
  useEffect(() => {
    if (!gekozen) return;
    const loslaten = (event: globalThis.PointerEvent) => {
      if (!(event.target instanceof Node) || !tabel.current?.contains(event.target))
        setGekozen(null);
    };
    document.addEventListener("pointerdown", loslaten);
    return () => document.removeEventListener("pointerdown", loslaten);
  }, [gekozen]);

  function dragEnd(event: DragEndEvent) {
    const { source, target } = event.operation;
    if (event.canceled || !source || !target) return;
    // SAFETY: every draggable here registers its roosterregel, every droppable its DropData.
    const [regel, to] = [source.data as Roosterregel, target.data as DropData];
    if (to.kind === "lesuur") {
      if (regel.weekdag === to.weekdag && regel.lesuurId === to.lesuurId) return;
      moveRegel({ regel, to: { weekdag: to.weekdag, lesuurId: to.lesuurId } });
      return;
    }
    const lesuur = dagrooster.lesuren.find((item) => item.id === regel.lesuurId);
    const matching = lesuur ? getMatchingLesuur(lesrooster, lesuur, to.weekdag) : undefined;
    if (to.weekdag === regel.weekdag || !matching) return;
    moveRegel({ regel, to: { weekdag: to.weekdag, lesuurId: matching.id } });
  }

  /** Drags the top or bottom edge of a row; the draft shows every step, letting go saves it. */
  function sleepRand(event: PointerEvent<HTMLElement>, rij: RoosterRij, rand: "begin" | "einde") {
    event.preventDefault();
    event.stopPropagation();
    const basis = lesrooster;
    const basisDagrooster = dagrooster;
    const startY = event.clientY;
    let laatste = basis;
    let lastDelta = 0;
    setSleep({ kind: "rand", id: rij.key, rand, basis: rij });
    const pointer = event.pointerId;
    const beweeg = (move: globalThis.PointerEvent) => {
      // Only the pointer that took hold of the edge moves it.
      if (move.pointerId !== pointer) return;
      const delta = Math.round((move.clientY - startY) / PX_PER_MINUUT / STAP) * STAP;
      if (delta === lastDelta) return;
      const next =
        rand === "einde"
          ? resizeEinde(basisDagrooster, rij.key, rij.eindMinuten + delta)
          : moveBegin(basisDagrooster, rij.key, rij.startMinuten + delta);
      if (!next) return;
      lastDelta = delta;
      laatste = withDagrooster(basis, next);
      setDraft(laatste);
    };
    const klaar = () => {
      window.removeEventListener("pointermove", beweeg);
      window.removeEventListener("pointerup", klaar);
      window.removeEventListener("pointercancel", klaar);
      setSleep(null);
      if (laatste !== basis) commit(laatste);
      else setDraft(null);
    };
    window.addEventListener("pointermove", beweeg);
    window.addEventListener("pointerup", klaar);
    window.addEventListener("pointercancel", klaar);
  }

  function randMetToetsen(
    event: KeyboardEvent<HTMLElement>,
    rij: RoosterRij,
    rand: "begin" | "einde",
  ) {
    const delta = event.key === "ArrowUp" ? -STAP : event.key === "ArrowDown" ? STAP : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next =
      rand === "einde"
        ? resizeEinde(dagrooster, rij.key, rij.eindMinuten + delta)
        : moveBegin(dagrooster, rij.key, rij.startMinuten + delta);
    if (next) commitDagrooster(next);
  }

  /** The row boundary a pointer is nearest: before which row a dragged pauze would land. */
  function voorIndex(clientY: number) {
    const index = rijen.findIndex((rij) => {
      const box = rijRefs.current.get(rij.key)?.getBoundingClientRect();
      return box ? clientY < box.top + box.height / 2 : false;
    });
    return index < 0 ? rijen.length : index;
  }

  /** Drags a pauze by its handle; a press without moving stays a click, which opens its menu. */
  function sleepPauze(event: PointerEvent<HTMLElement>, pauze: Pauze, onGesleept: () => void) {
    if (event.button !== 0) return;
    event.preventDefault();
    verplaatsPauze(pauze, event.pointerId, event.clientY, false, onGesleept);
  }

  /**
   * On a touch screen the pauze itself is picked up by holding it, as a leerling in the groepjes
   * is: a finger that moves first scrolls the rooster instead.
   */
  function houdPauze(event: PointerEvent<HTMLElement>, pauze: Pauze) {
    if (event.pointerType !== "touch") return;
    const pointer = event.pointerId;
    const { clientX: startX, clientY: startY } = event;
    const stop = () => {
      window.clearTimeout(opgepakt);
      window.removeEventListener("pointermove", beweeg);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    const beweeg = (move: globalThis.PointerEvent) => {
      if (move.pointerId !== pointer) return;
      if (Math.hypot(move.clientX - startX, move.clientY - startY) > HOUD_SPELING) stop();
    };
    const opgepakt = window.setTimeout(() => {
      stop();
      verplaatsPauze(pauze, pointer, startY, true, () => {});
    }, HOUD_MS);
    window.addEventListener("pointermove", beweeg);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  }

  function verplaatsPauze(
    pauze: Pauze,
    pointer: number,
    startY: number,
    opgepakt: boolean,
    onGesleept: () => void,
  ) {
    let voor: number | null = null;
    const toon = (clientY: number) => {
      if (voor === null) setGekozen(pauze.id);
      voor = voorIndex(clientY);
      setSleep({ kind: "verplaats", id: pauze.id, voor });
    };
    // Held, it moves from the start; the page must not scroll under the finger that moves it.
    const geenScroll = (touch: TouchEvent) => touch.preventDefault();
    if (opgepakt) {
      toon(startY);
      window.addEventListener("touchmove", geenScroll, { passive: false });
    }
    const beweeg = (move: globalThis.PointerEvent) => {
      if (move.pointerId !== pointer) return;
      if (voor === null && Math.abs(move.clientY - startY) < SLEEP_DREMPEL) return;
      toon(move.clientY);
    };
    const klaar = () => {
      window.removeEventListener("pointermove", beweeg);
      window.removeEventListener("pointerup", klaar);
      window.removeEventListener("pointercancel", klaar);
      window.removeEventListener("touchmove", geenScroll);
      setSleep(null);
      if (voor === null) return;
      onGesleept();
      const next = movePauze(dagrooster, pauze.id, voor);
      if (next && next !== dagrooster) commitDagrooster(next);
    };
    window.addEventListener("pointermove", beweeg);
    window.addEventListener("pointerup", klaar);
    window.addEventListener("pointercancel", klaar);
  }

  function pauzeMetToetsen(event: KeyboardEvent<HTMLElement>, pauze: Pauze) {
    const index = rijen.findIndex((rij) => rij.key === pauze.id);
    const voor = event.key === "ArrowUp" ? index - 1 : event.key === "ArrowDown" ? index + 2 : null;
    if (voor === null || voor < 0 || voor > rijen.length) return;
    event.preventDefault();
    const next = movePauze(dagrooster, pauze.id, voor);
    if (next && next !== dagrooster) commitDagrooster(next);
  }

  const insertTussen = (vorige: RoosterRij) => {
    const next = insertPauze(
      dagrooster,
      randomUuid(),
      m["planning.rooster.pauze.nieuw"](),
      vorige.key,
    );
    if (next) commitDagrooster(next);
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <DragDropProvider onDragEnd={dragEnd}>
        <div
          ref={tabel}
          className="flex min-w-0 flex-col text-xs"
          role="table"
          aria-label={m["planning.rooster.title"]()}
          data-testid="rooster-tabel"
          onKeyDown={(event) => {
            if (event.key === "Escape" && gekozen) {
              event.stopPropagation();
              setGekozen(null);
            }
          }}
        >
          {/* A phone has no room next to the times: the days get a row of their own. */}
          <div role="row" className={cn(RIJ, "flex items-center max-md:pb-2.5 md:h-11.5")}>
            <div role="columnheader" className={cn(KOP, "text-muted-foreground max-md:hidden")}>
              {m["planning.rooster.lesuur_kolom"]()}
            </div>
            {/* Padded like a cell, so the days line up with the lessen under them. */}
            <div role="columnheader" className="min-w-0 flex-1 px-0.75 max-md:pl-(--rooster-rand)">
              <Tabs
                value={weekdag}
                onValueChange={(value) => {
                  const next = weekdagSchema.safeParse(value);
                  if (next.success) onWeekdagChange(next.data);
                }}
                className="w-auto"
              >
                <TabsList
                  aria-label={m["planning.rooster.dag"]()}
                  className="w-full group-data-[orientation=horizontal]/tabs:h-8.5"
                >
                  {SCHOOLDAGEN.map((item) => (
                    <WeekdagTab key={item} weekdag={item} />
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
          {rijen.map((rij, index) => {
            const vorige = rijen[index - 1];
            const gat = vorige
              ? Math.min(
                  MAX_GAT,
                  Math.max(0, (rij.startMinuten - vorige.eindMinuten) * PX_PER_MINUUT),
                )
              : 0;
            const isGekozen = gekozen === rij.key;
            const randSleep = sleep?.kind === "rand" && sleep.id === rij.key ? sleep : null;
            const sleeptHier = sleep?.id === rij.key;
            // The line between two lesuren is where a pauze goes in, unless a row is picked there.
            const kanInvoegen =
              rij.kind === "lesuur" &&
              vorige?.kind === "lesuur" &&
              gekozen !== rij.key &&
              gekozen !== vorige.key &&
              sleep === null;
            return (
              <div
                key={rij.key}
                ref={(element) => {
                  if (element) rijRefs.current.set(rij.key, element);
                  else rijRefs.current.delete(rij.key);
                }}
                role="row"
                className={cn(
                  RIJ,
                  "relative isolate flex shrink-0 items-center border-t transition-colors",
                  // The grips stick out over the rows around it.
                  (isGekozen || sleeptHier) && "z-10",
                  sleep?.kind === "verplaats" && sleeptHier && "opacity-60",
                )}
                data-lesuur={rij.kind === "lesuur" ? rij.lesuur.nummer : undefined}
                data-pauze={rij.kind === "pauze" ? rij.pauze.naam : undefined}
                style={{ height: hoogteVan(rij), marginTop: gat }}
                data-gekozen={isGekozen || undefined}
              >
                {isGekozen || sleeptHier ? (
                  <span
                    aria-hidden
                    className={cn(
                      "bg-muted ring-ring pointer-events-none absolute inset-y-0 left-0 -z-10 rounded-r-md ring-1 ring-inset",
                      "right-[calc(var(--rooster-rand)+var(--rooster-vrij,0px))]",
                    )}
                  />
                ) : null}
                {randSleep ? <RandFeedback rij={rij} sleep={randSleep} /> : null}
                {sleep?.kind === "verplaats" && sleep.voor === index ? <InvoegLijn boven /> : null}
                {kanInvoegen && vorige ? (
                  <PauzeInvoegen naam={rijNaam(vorige)} onInvoegen={() => insertTussen(vorige)} />
                ) : null}
                {rij.kind === "lesuur" ? (
                  <LesuurKop
                    lesrooster={lesrooster}
                    dagrooster={dagrooster}
                    lesuur={rij.lesuur}
                    toonDuur={sleep?.kind === "rand"}
                    onKies={() => setGekozen(rij.key)}
                  />
                ) : (
                  <PauzeKop
                    lesrooster={lesrooster}
                    dagrooster={dagrooster}
                    pauze={rij.pauze}
                    toonDuur={sleep?.kind === "rand"}
                    onKies={() => setGekozen(rij.key)}
                    onSleep={sleepPauze}
                    onToets={pauzeMetToetsen}
                  />
                )}
                {rij.kind === "lesuur" ? (
                  <RoosterCel
                    lesuur={rij.lesuur}
                    weekdagName={weekdagName(weekdag)}
                    regels={regels.filter(
                      (regel) => regel.weekdag === weekdag && regel.lesuurId === rij.lesuur.id,
                    )}
                    klassen={klassen}
                    docenten={docenten}
                  />
                ) : (
                  <PauzeCel pauze={rij.pauze} onHoud={houdPauze} />
                )}
                {isGekozen || (sleep?.kind === "rand" && sleeptHier) ? (
                  <>
                    <RandGreep
                      rand="begin"
                      label={m["planning.rooster.begin_slepen"]({ naam: rijNaam(rij) })}
                      onPointerDown={(event) => sleepRand(event, rij, "begin")}
                      onKeyDown={(event) => randMetToetsen(event, rij, "begin")}
                    />
                    <RandGreep
                      rand="einde"
                      label={m["planning.rooster.einde_slepen"]({ naam: rijNaam(rij) })}
                      onPointerDown={(event) => sleepRand(event, rij, "einde")}
                      onKeyDown={(event) => randMetToetsen(event, rij, "einde")}
                    />
                  </>
                ) : null}
                {sleep?.kind === "verplaats" &&
                sleep.voor === rijen.length &&
                index === rijen.length - 1 ? (
                  <InvoegLijn />
                ) : null}
              </div>
            );
          })}
        </div>
      </DragDropProvider>
      <div className="mt-3 flex gap-4.5">
        <Button
          type="button"
          variant="link"
          className="h-auto px-0 text-[13px] font-semibold"
          onClick={() => commitDagrooster(addLesuur(dagrooster, randomUuid()))}
        >
          <IconMaterialSymbolsAdd />
          {m["planning.rooster.lesuur_toevoegen"]()}
        </Button>
        <Button
          type="button"
          variant="link"
          className="h-auto px-0 text-[13px] font-semibold"
          onClick={() =>
            commitDagrooster(
              addPauze(dagrooster, randomUuid(), m["planning.rooster.pauze.nieuw"]()),
            )
          }
        >
          <IconMaterialSymbolsAdd />
          {m["planning.rooster.pauze_toevoegen"]()}
        </Button>
      </div>
    </div>
  );
}

/** A weekdag of the rooster to switch to; a les dropped on it moves to that day. */
function WeekdagTab({ weekdag }: { weekdag: number }) {
  const { ref, isDropTarget } = useDroppable({
    id: `weekdag|${weekdag}`,
    data: { kind: "weekdag", weekdag } satisfies DropData,
  });
  return (
    <TabsTrigger
      ref={ref}
      value={weekdag}
      className={cn(
        "flex-1 px-3.5 text-[13px] capitalize data-active:font-semibold max-md:px-1",
        isDropTarget && "bg-primary/10 text-primary",
      )}
      data-weekdag={weekdag}
    >
      <span className="md:hidden">{weekdagKort(weekdag)}</span>
      <span className="max-md:hidden">{weekdagName(weekdag)}</span>
    </TabsTrigger>
  );
}

/**
 * While an edge is dragged: a dashed line where it was, and the new times with the change in
 * minutes next to the edge.
 */
function RandFeedback({
  rij,
  sleep,
}: {
  rij: RoosterRij;
  sleep: Extract<Sleep, { kind: "rand" }>;
}) {
  const verschil =
    sleep.rand === "einde"
      ? rij.eindMinuten - sleep.basis.eindMinuten
      : rij.startMinuten - sleep.basis.startMinuten;
  // The top edge takes the whole row along, the row before it growing or shrinking.
  const oud =
    sleep.rand === "einde"
      ? { top: hoogteVan(sleep.basis) }
      : { top: -Math.round(verschil * PX_PER_MINUUT) };
  return (
    <>
      {verschil !== 0 ? (
        <span
          aria-hidden
          className="border-primary/60 pointer-events-none absolute left-[calc(11.5rem+var(--rooster-rand))] right-[calc(var(--rooster-rand)+var(--rooster-vrij,0px))] z-20 border-t border-dashed"
          style={oud}
        />
      ) : null}
      <span
        role="status"
        className={cn(
          "bg-primary text-primary-foreground pointer-events-none absolute right-[calc(var(--rooster-rand)+var(--rooster-vrij,0px)+0.5rem)] z-30 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap tabular-nums shadow-sm",
          sleep.rand === "einde" ? "-bottom-2.5" : "-top-2.5",
        )}
      >
        {m["planning.rooster.sleep_label"]({
          van: minutesToLabel(rij.startMinuten),
          tot: minutesToLabel(rij.eindMinuten),
          duur: duurVan(rij),
        })}
        {verschil !== 0 ? ` (${verschil > 0 ? "+" : "−"}${Math.abs(verschil)})` : null}
      </span>
    </>
  );
}

/** Where a dragged pauze lands. */
function InvoegLijn({ boven = false }: { boven?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "bg-primary pointer-events-none absolute inset-x-0 z-20 h-0.5 rounded-full",
        boven ? "-top-px" : "-bottom-px",
      )}
    />
  );
}

/** The line between two lesuren: pointing at it offers a pauze there. */
function PauzeInvoegen({ naam, onInvoegen }: { naam: string; onInvoegen: () => void }) {
  return (
    <div className="group/invoeg absolute inset-x-0 -top-2 z-10 flex h-4 items-center justify-center">
      <span className="bg-primary pointer-events-none absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover/invoeg:opacity-100 group-focus-within/invoeg:opacity-100" />
      <Button
        size="xxs"
        aria-label={m["planning.rooster.pauze_invoegen"]({ naam })}
        onClick={onInvoegen}
        className="rounded-full text-[11px] opacity-0 transition-opacity group-hover/invoeg:opacity-100 focus-visible:opacity-100"
        contentClassName="gap-0.5"
      >
        <IconMaterialSymbolsAdd className="size-3.5" />
        {m["planning.rooster.pauze.nieuw"]()}
      </Button>
    </div>
  );
}

/** A grip on the top or bottom edge of the picked row. */
function RandGreep({
  rand,
  label,
  onPointerDown,
  onKeyDown,
}: {
  rand: "begin" | "einde";
  label: string;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}) {
  return (
    <span className="pointer-events-none absolute inset-y-0 left-[calc(11.5rem+var(--rooster-rand))] right-[calc(var(--rooster-rand)+var(--rooster-vrij,0px))] z-20 supports-[-webkit-touch-callout:none]:left-[calc(13rem+var(--rooster-rand))]">
      <button
        type="button"
        aria-label={label}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        className={cn(
          "bg-background ring-ring pointer-events-auto absolute left-1/2 h-1.5 w-8 -translate-x-1/2 cursor-ns-resize touch-none rounded-[3px] shadow-sm ring-1",
          focusStyles,
          "transition-colors hover:bg-primary/15 before:absolute before:-inset-2",
          rand === "begin" ? "-top-0.75" : "-bottom-0.75",
        )}
        data-base-ui-swipe-ignore
        data-rand={rand}
      />
    </span>
  );
}

/** A time field that only lets a time go that leaves every lesuur and pauze long enough. */
function TijdVeld({
  value,
  label,
  look = "veld",
  onChange,
}: {
  value: number;
  label: string;
  /** A field, or muted text that only shows it is a field when pointed at. */
  look?: "veld" | "tekst";
  /** Answers whether the time fits. */
  onChange: (minutes: number) => boolean;
}) {
  const [text, setText] = useState(minutesToLabel(value));
  const [synced, setSynced] = useState(value);
  const [fits, setFits] = useState(true);
  if (synced !== value) {
    setSynced(value);
    setText(minutesToLabel(value));
    setFits(true);
  }
  const stap = (event: KeyboardEvent<HTMLInputElement>) => {
    const richting = event.key === "ArrowUp" ? 1 : event.key === "ArrowDown" ? -1 : 0;
    if (richting === 0) return;
    event.preventDefault();
    const grootte = event.shiftKey ? 15 : STAP;
    const huidig = Option.getOrElse(labelToMinutes(text), () => value);
    // Onto the grid first: 08:32 goes up to 08:35, not 08:37.
    const next =
      richting > 0
        ? Math.floor(huidig / grootte) * grootte + grootte
        : Math.ceil(huidig / grootte) * grootte - grootte;
    if (!onChange(next)) return;
    setText(minutesToLabel(next));
    setFits(true);
  };
  return (
    <Input
      role="spinbutton"
      inputMode="numeric"
      maxLength={5}
      placeholder="08:30"
      aria-label={label}
      aria-valuenow={value}
      aria-valuetext={minutesToLabel(value)}
      onKeyDown={stap}
      aria-invalid={!fits || undefined}
      title={fits ? undefined : m["planning.rooster.tijd_ongeldig"]()}
      value={text}
      className={cn(
        "h-6.5 shrink-0 rounded-md text-center text-xs tabular-nums shadow-none md:text-xs",
        // One width for every look, so the times and the cross line up in every row. iOS sets an
        // input's text to 16px, so it does not zoom in on it (ui's index.css): the time needs room.
        "w-[3.25rem] px-1.5 supports-[-webkit-touch-callout:none]:h-7.5 supports-[-webkit-touch-callout:none]:w-16",
        "hover:border-foreground/25 transition-colors",
        look === "tekst" && "text-muted-foreground border-transparent",
      )}
      onChange={(event) => {
        setText(event.target.value);
        const minutes = labelToMinutes(event.target.value);
        if (Option.isSome(minutes)) setFits(onChange(minutes.value));
      }}
      onBlur={() => {
        if (!fits) {
          setText(minutesToLabel(value));
          setFits(true);
        }
      }}
    />
  );
}

/** Clicking the head of a row picks it, so its edges can be dragged; not when that click edits it. */
const kiesBijKlik = (onKies: () => void) => (event: React.MouseEvent<HTMLElement>) => {
  if (event.target instanceof Element && event.target.closest("input, button")) return;
  onKies();
};

/** How long a row lasts, shown on every row while an edge is dragged to compare with. */
function Duur({ minuten }: { minuten: number }) {
  return (
    <span className="text-muted-foreground ml-auto shrink-0 pr-1 text-[11px] tabular-nums">
      {m["planning.rooster.duur"]({ minuten })}
    </span>
  );
}

function VerwijderRij({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <IconButton
      size="icon-xs"
      label={label}
      className={cn(
        "hover:bg-destructive/10 hover:text-destructive ml-auto shrink-0 opacity-0 transition-[opacity,background-color,color] group-hover/kop:opacity-100 focus-visible:opacity-100",
        // Without hover there is nothing to bring it out: always there, with room around it to tap.
        "pointer-coarse:opacity-100 pointer-coarse:before:absolute pointer-coarse:before:-inset-2.5",
      )}
      onClick={(event) => {
        // From the keyboard, focus goes on to the next row's cross instead of the page.
        if (event.currentTarget.matches(":focus-visible")) {
          const rij = event.currentTarget.closest('[role="row"]');
          const naast = rij?.nextElementSibling ?? rij?.previousElementSibling;
          naast?.querySelector<HTMLElement>("[data-verwijder-rij]")?.focus();
        }
        onClick();
      }}
      data-verwijder-rij
    >
      <IconMaterialSymbolsCloseRounded className="size-4" />
    </IconButton>
  );
}

function LesuurKop({
  lesrooster,
  dagrooster,
  lesuur,
  toonDuur,
  onKies,
}: {
  lesrooster: Lesrooster;
  dagrooster: Dagrooster;
  lesuur: Lesuur;
  toonDuur: boolean;
  onKies: () => void;
}) {
  const change = useAtomSet(changeLesroosterAtom);
  const commit = useAtomSet(commitLesroosterAtom);
  const naam = formatLesuur(lesuur);
  const eind = lesuur.startMinuten + lesuur.duurMinuten;
  const zet = (next: Dagrooster | null) => {
    if (next) change(withDagrooster(lesrooster, next));
    return next !== null;
  };
  return (
    <div
      role="rowheader"
      className={cn(
        KOP,
        "group/kop flex h-full cursor-pointer items-center gap-1 rounded-r-md pr-1",
      )}
      onClick={kiesBijKlik(onKies)}
      onFocus={onKies}
    >
      <span className="font-heading w-7 shrink-0 pl-0.5 text-sm font-extrabold">
        {m["planning.rooster.lesuur_kort"]({ nummer: lesuur.nummer })}
      </span>
      <TijdVeld
        value={lesuur.startMinuten}
        label={m["planning.rooster.begin_van"]({ naam })}
        onChange={(start) => zet(moveBegin(dagrooster, lesuur.id, start))}
      />
      <span className="text-muted-foreground">–</span>
      <TijdVeld
        value={eind}
        look="tekst"
        label={m["planning.rooster.einde_van"]({ naam })}
        onChange={(end) => zet(resizeEinde(dagrooster, lesuur.id, end))}
      />
      {toonDuur ? (
        <Duur minuten={lesuur.duurMinuten} />
      ) : (
        <VerwijderRij
          label={m["planning.rooster.lesuur_verwijderen"]({ naam })}
          onClick={() => commit(withDagrooster(lesrooster, removeLesuur(dagrooster, lesuur.id)))}
        />
      )}
    </div>
  );
}

function PauzeKop({
  lesrooster,
  dagrooster,
  pauze,
  toonDuur,
  onKies,
  onSleep,
  onToets,
}: {
  lesrooster: Lesrooster;
  dagrooster: Dagrooster;
  pauze: Pauze;
  toonDuur: boolean;
  onKies: () => void;
  onSleep: (event: PointerEvent<HTMLElement>, pauze: Pauze, onGesleept: () => void) => void;
  onToets: (event: KeyboardEvent<HTMLElement>, pauze: Pauze) => void;
}) {
  const change = useAtomSet(changeLesroosterAtom);
  const commit = useAtomSet(commitLesroosterAtom);
  const [menu, setMenu] = useState(false);
  // The click that ends a drag is not a click on the handle.
  const gesleept = useRef(false);
  const [naam, setNaam] = useState(pauze.naam);
  const eind = pauze.startMinuten + pauze.duurMinuten;
  const zet = (next: Dagrooster | null) => {
    if (next) change(withDagrooster(lesrooster, next));
    return next !== null;
  };
  const verwijder = () => commit(withDagrooster(lesrooster, removePauze(dagrooster, pauze.id)));
  const hernoem = () => {
    if (naam.trim() && naam.trim() !== pauze.naam) {
      commit(withDagrooster(lesrooster, renamePauze(dagrooster, pauze.id, naam.trim())));
    }
  };
  return (
    <div
      role="rowheader"
      className={cn(
        KOP,
        "group/kop flex h-full cursor-pointer items-center gap-1 rounded-r-md pr-1",
      )}
      onClick={kiesBijKlik(onKies)}
      onFocus={onKies}
    >
      <Popover
        open={menu}
        onOpenChange={(open) => {
          if (open && gesleept.current) {
            gesleept.current = false;
            return;
          }
          // Closing unmounts the field before it blurs, so the name is saved here; opening
          // starts from the saved name, also after an undo.
          if (open) setNaam(pauze.naam);
          else hernoem();
          setMenu(open);
        }}
      >
        {/* As wide as a lesuur's "1e" and centred on it, in the same room. */}
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label={m["planning.rooster.pauze_verplaatsen"]({ naam: pauze.naam })}
              title={m["planning.rooster.pauze_greep_uitleg"]()}
              className={cn(
                "text-muted-foreground hover:bg-foreground/8 hover:text-foreground mr-2 flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-md active:cursor-grabbing",
                focusStyles,
                "transition-colors",
              )}
              onPointerDown={(event) => {
                gesleept.current = false;
                onSleep(event, pauze, () => {
                  gesleept.current = true;
                });
              }}
              onKeyDown={(event) => onToets(event, pauze)}
              data-base-ui-swipe-ignore
              data-pauze-greep
            >
              <IconMaterialSymbolsDragIndicator className="size-4" />
            </button>
          }
        />
        <PopoverContent align="start" className="w-64 gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium">
            {m["planning.rooster.pauze.naam"]()}
            <Input
              value={naam}
              maxLength={50}
              onChange={(event) => setNaam(event.target.value)}
              onBlur={hernoem}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                hernoem();
                setMenu(false);
              }}
            />
          </label>
          <Button
            type="button"
            variant="outline"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              setMenu(false);
              verwijder();
            }}
          >
            <IconMaterialSymbolsDeleteOutline />
            {m["planning.rooster.pauze_verwijderen"]()}
          </Button>
        </PopoverContent>
      </Popover>
      <TijdVeld
        value={pauze.startMinuten}
        label={m["planning.rooster.begin_van"]({ naam: pauze.naam })}
        onChange={(start) => zet(moveBegin(dagrooster, pauze.id, start))}
      />
      <span className="text-muted-foreground">–</span>
      <TijdVeld
        value={eind}
        look="tekst"
        label={m["planning.rooster.einde_van"]({ naam: pauze.naam })}
        onChange={(end) => zet(resizeEinde(dagrooster, pauze.id, end))}
      />
      {toonDuur ? (
        <Duur minuten={pauze.duurMinuten} />
      ) : (
        <VerwijderRij
          label={m["planning.rooster.pauze_verwijderen_naam"]({ naam: pauze.naam })}
          onClick={verwijder}
        />
      )}
    </div>
  );
}

/** A pauze of the day as a bar with its name, as wide as the lessen next to it. */
function PauzeCel({
  pauze,
  onHoud,
}: {
  pauze: Pauze;
  onHoud: (event: PointerEvent<HTMLElement>, pauze: Pauze) => void;
}) {
  return (
    <div role="cell" className="flex h-full min-w-0 flex-1 px-0.75 py-1">
      <span
        className="flex h-full min-w-0 flex-1 items-center truncate rounded-md bg-zinc-400 pl-2 text-[11px] font-semibold text-white select-none [-webkit-touch-callout:none] dark:bg-zinc-600"
        onPointerDown={(event) => onHoud(event, pauze)}
        // A drawer around the rooster leaves the pauze to the finger that moves it.
        data-base-ui-swipe-ignore
        data-pauze-balk
      >
        {pauze.naam}
      </span>
    </div>
  );
}
